import type { FormEvent, MouseEvent } from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { GLOBAL_SEARCH_INITIAL_STATE, GlobalSearchStateActions, globalSearchStateReducer } from 'contexts/GlobalSearch/GlobalSearchState'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import SearchDateInputDesktop from 'components/Search/SearchForm/SearchDateInput/SearchDateInputDesktop'
import TripContextSearchDateInput from './TripContextSearchDateInput'
import SearchTravellersInputDesktop from 'components/Search/SearchForm/SearchTravellersInput/SearchTravellersInputDesktop'
import ExperiencesSearchDateSelectDropdown from 'components/Search/Experiences/ExperiencesSearchDateSelectDropdown'
import TextButton from 'LuxKit/Button/TextButton'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import { SearchMenuStates } from 'components/Search/type'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { TRIP_EXPERIENCE_TYPEAHEAD_SEARCH_TYPES, TRIP_PLACE_TYPEAHEAD_TYPES } from 'tripPlanner/config/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getRecentSearches } from 'components/Search/utils'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import zIndex from 'styles/tools/z-index'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useElementWidth } from 'hooks/useElementSize'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { addFlexibleMonthsIfNoneSelected } from 'lib/search/searchUtils'
import ListingDrawerMobileHotelDateFields from './ListingDrawerMobileHotelDateFields'
import ListingDrawerMobileHotelGuestsFields from './ListingDrawerMobileHotelGuestsFields'
import ListingDrawerMobileExperienceDateFields from './ListingDrawerMobileExperienceDateFields'
import type { TripStop } from 'tripPlanner/api/types'
import useListingDrawerHotelSearchDraft from '../hooks/useListingDrawerHotelSearchDraft'
import {
  isListingDrawerHotelDateSelection,
  type ListingDrawerHotelDateSelection,
  type ListingDrawerHotelFlexibleNights,
} from './ListingDrawerSearchDates'

export type DrawerSortValue = Experiences.SortTypes

export interface ListingDrawerSearchSubmitParams {
  searchValue: string
  searchItem?: App.SearchItem
  hotelDateSelection: ListingDrawerHotelDateSelection
  occupancies: Array<App.Occupants>
}

interface Props {
  searchLabel: string
  searchValue: string
  searchItem?: App.SearchItem
  searchPlaceholder: string
  onSearch: (searchValue: string, searchItem?: App.SearchItem) => void
  searchVertical?: App.SearchPlaceVertical
  checkIn?: string
  checkOut?: string
  onDateChange?: (dates: { checkIn?: string, checkOut?: string }) => void
  hotelDateSelection?: ListingDrawerHotelDateSelection
  onHotelDateSelectionChange?: (selection: ListingDrawerHotelDateSelection, target?: App.SearchItem) => void
  guests?: number
  rooms?: Array<App.Occupants>
  maxGuests?: number
  onGuestsChange?: (guests: number) => void
  onRoomsChange?: (rooms: Array<App.Occupants>) => void
  showCheckout?: boolean
  showGuests?: boolean
  /** False where the destination is fixed. The destination props are still supplied and
   *  submitted; the traveller just cannot retarget the search. */
  showDestinationField?: boolean
  onSearchItemChange?: (searchItem?: App.SearchItem) => void
  whenLabel?: string
  experienceTripDateRange?: { start: string, end: string }
  /**
   * Month the date calendars should open on when nothing has been picked yet.
   * Only moves the viewport — it never selects a date.
   */
  searchCalendarFocusDate?: Date
  /**
   * The trip's stops, drawn behind the desktop date calendar as read-only
   * context so the dates already committed elsewhere on the itinerary are
   * visible while these ones are chosen. Omitted, the standard search
   * calendar is used unchanged.
   */
  tripStops?: ReadonlyArray<TripStop>
  /** The stop this search belongs to, so its bar can hold the foreground. */
  contextStopId?: number
  onSearchSubmit?: (params: ListingDrawerSearchSubmitParams) => void
  /**
   * Use the mobile date/guest fields whenever the controls themselves are narrower
   * than this. Drawers can be far narrower than the viewport, so they opt in here
   * rather than leaving the choice to the screen size.
   */
  compactBelowWidth?: number
}

export interface ListingDrawerSearchControlsHandle {
  openHotelDatePicker: (searchItem?: App.SearchItem) => void
}

function reduceSearchState(state: GlobalSearchState, action: GlobalSearchAction): GlobalSearchState {
  return globalSearchStateReducer(state, action)
}

const HOTEL_FLEXIBLE_NIGHTS: ReadonlySet<string> = new Set(
  Object.values(DEFAULT_FLEXIBLE_DURATION_RANGE).filter(
    (value): value is ListingDrawerHotelFlexibleNights =>
      value !== DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY,
  ),
)

function isHotelFlexibleNights(
  value: string,
): value is ListingDrawerHotelFlexibleNights {
  return HOTEL_FLEXIBLE_NIGHTS.has(value)
}

function getDateSearchOptionId(
  selection: ListingDrawerHotelDateSelection,
): DATE_SEARCH_OPTION_IDS {
  if (selection.type === 'specific') return DATE_SEARCH_OPTION_IDS.SPECIFIC
  if (selection.type === 'flexible') return DATE_SEARCH_OPTION_IDS.FLEXIBLE
  return DATE_SEARCH_OPTION_IDS.ANYTIME
}

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  align-items: stretch;
`

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  position: relative;
  z-index: ${zIndex.menuInModal};
  width: 100%;
`

const SearchFieldContainer = styled.div`
  flex: 1 1 ${rem(260)};
  width: auto;
`

const SearchField = styled(SearchLocationInput)`
  width: 100%;
`

const DateInputContainer = styled.div<{ $compact: boolean }>`
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;

  ${(props) => !props.$compact && css`
    ${mediaQueryUp.tablet} {
      flex: 0 1 ${rem(360)};
      min-width: ${rem(300)};
      width: auto;
    }
  `}
`

const ExperienceDateInputContainer = styled.div`
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 1 1 ${rem(220)};
    min-width: ${rem(180)};
    width: auto;
  }
`

const TravellersInputContainer = styled.div<{ $compact: boolean }>`
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;

  ${(props) => !props.$compact && css`
    ${mediaQueryUp.tablet} {
      flex: 0 1 ${rem(220)};
      min-width: ${rem(180)};
      width: auto;
    }
  `}
`

const StyledSearchFormField = styled(SearchFormField)`
  width: 100%;
`

const SearchButton = styled(TextButton)<{ $compact: boolean }>`
  flex: 1 1 100%;
  width: 100%;

  ${(props) => !props.$compact && css`
    ${mediaQueryUp.tablet} {
      flex: 0 1 auto;
      width: auto;
    }
  `}
`

type DisplayOnlySearchItem = App.SearchDestination & { displayOnlySearchItem: true }

function isDisplayOnlySearchItem(searchItem?: App.SearchItem): searchItem is DisplayOnlySearchItem {
  return searchItem?.searchType === 'destination' && 'displayOnlySearchItem' in searchItem
}

function buildDisplayOnlySearchItem(label: string): DisplayOnlySearchItem | undefined {
  const trimmedLabel = label.trim()
  if (!trimmedLabel) return undefined

  return {
    searchType: 'destination',
    value: trimmedLabel,
    displayOnlySearchItem: true,
    format: {
      mainText: trimmedLabel,
    },
  }
}

function getSubmitSearchItem(searchValue: string, searchItem?: App.SearchItem): App.SearchItem | undefined {
  const trimmedSearchValue = searchValue.trim()
  if (!searchItem || searchItem.format.mainText.trim() !== trimmedSearchValue) return undefined

  if (isDisplayOnlySearchItem(searchItem)) return undefined

  return searchItem
}

function createOccupancy(guests?: number): App.Occupants {
  return {
    adults: guests ?? 2,
  }
}

export function getHotelDateSelectionFromSearchState(
  searchState: Pick<GlobalSearchState,
  | 'dateSearchOptionId'
  | 'checkinDate'
  | 'checkoutDate'
  | 'flexibleMonths'
  | 'flexibleNights'
  | 'userSelectedFlexibleMonths'
  >,
  searchVertical?: App.SearchPlaceVertical,
): ListingDrawerHotelDateSelection {
  if (searchState.checkinDate && (searchState.checkoutDate || searchVertical === 'experience')) {
    return {
      type: 'specific',
      checkIn: searchState.checkinDate.format(ISO_DATE_FORMAT),
      checkOut: (searchState.checkoutDate ?? searchState.checkinDate).format(ISO_DATE_FORMAT),
    }
  }

  if (searchState.dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
    const flexibleNights = isHotelFlexibleNights(searchState.flexibleNights)
      ? searchState.flexibleNights
      : DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
    return {
      type: 'flexible',
      flexibleMonths: addFlexibleMonthsIfNoneSelected(
        flexibleNights,
        searchState.flexibleMonths,
      ),
      flexibleNights,
      userSelectedFlexibleMonths: searchState.userSelectedFlexibleMonths,
    }
  }

  return { type: 'anytime' }
}

const ListingDrawerSearchControls = forwardRef<ListingDrawerSearchControlsHandle, Props>(function ListingDrawerSearchControls({
  searchLabel,
  searchValue,
  searchItem,
  searchPlaceholder,
  onSearch,
  searchVertical,
  checkIn,
  checkOut,
  onDateChange,
  hotelDateSelection,
  onHotelDateSelectionChange,
  guests,
  rooms: selectedRooms,
  maxGuests = 12,
  onGuestsChange,
  onRoomsChange,
  showCheckout = false,
  showGuests = false,
  showDestinationField = true,
  onSearchItemChange,
  whenLabel,
  experienceTripDateRange,
  searchCalendarFocusDate,
  tripStops,
  contextStopId,
  onSearchSubmit,
  compactBelowWidth,
}: Props, ref) {
  const intl = useIntl()
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const controlsWidth = useElementWidth(controlsContainerRef)
  const isNarrowViewport = !useScreenSizeUp('tablet')
  const isCompactDatePicker = compactBelowWidth && controlsWidth
    ? controlsWidth < compactBelowWidth
    : isNarrowViewport
  const pendingDateTargetRef = useRef<App.SearchItem | undefined>(undefined)
  const [draftSearch, setDraftSearch] = useState(searchValue)
  const [draftSearchItem, setDraftSearchItem] = useState<App.SearchItem | undefined>(undefined)
  const defaultRooms = useMemo(
    () => selectedRooms ?? [createOccupancy(guests)],
    [guests, selectedRooms],
  )
  // Seeded party from flights/chat may include children without ages (-1 placeholders).
  // Treat that like a failed Apply: show Guests error + open the picker once.
  // Mid-edit invalid occupancy (e.g. adding a child) does not show the banner until Apply/Search.
  const initiallyIncompleteOccupancy = showGuests &&
    defaultRooms.length > 0 &&
    !isValidOccupancy(...defaultRooms)
  const committedHotelDateSelection = useMemo<ListingDrawerHotelDateSelection>(() => {
    if (hotelDateSelection) return hotelDateSelection
    return checkIn && checkOut
      ? { type: 'specific', checkIn, checkOut }
      : { type: 'anytime' }
  }, [checkIn, checkOut, hotelDateSelection])
  const [searchState, searchDispatch] = useReducer(
    reduceSearchState,
    {
      ...GLOBAL_SEARCH_INITIAL_STATE,
      searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue),
      checkinDate: committedHotelDateSelection.type === 'specific' ? moment(committedHotelDateSelection.checkIn) : undefined,
      checkoutDate: committedHotelDateSelection.type === 'specific' ? moment(committedHotelDateSelection.checkOut) : undefined,
      flexibleMonths: committedHotelDateSelection.type === 'flexible' ? committedHotelDateSelection.flexibleMonths : undefined,
      flexibleNights: committedHotelDateSelection.type === 'flexible' ? committedHotelDateSelection.flexibleNights : DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY,
      userSelectedFlexibleMonths: committedHotelDateSelection.type === 'flexible' && committedHotelDateSelection.userSelectedFlexibleMonths,
      dateSearchOptionId: getDateSearchOptionId(committedHotelDateSelection),
      isAnytimeDateSelected: committedHotelDateSelection.type === 'anytime',
      occupancies: defaultRooms,
      areRoomErrorsShown: initiallyIncompleteOccupancy,
      activeMenu: initiallyIncompleteOccupancy && !isCompactDatePicker
        ? SearchMenuStates.Rooms
        : SearchMenuStates.Closed,
    },
  )
  const searchInputRef = useRef<HTMLInputElement>(null)
  const experienceDateTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    pendingDateTargetRef.current = undefined
    setDraftSearch(searchValue)
    setDraftSearchItem(searchItem)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue),
    })
  }, [searchItem, searchValue])

  const rooms = useMemo(
    () => searchState.occupancies ?? defaultRooms,
    [defaultRooms, searchState.occupancies],
  )
  const areRoomsValid = rooms.length > 0 && sumUpOccupancies(rooms) <= maxGuests && isValidOccupancy(...rooms)

  const clearRoomErrors = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: false })
  }, [searchDispatch])

  const discardPendingDateTarget = useCallback(() => {
    if (!pendingDateTargetRef.current) return
    pendingDateTargetRef.current = undefined
    setDraftSearch(searchValue)
    setDraftSearchItem(searchItem)
    searchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue) })
  }, [searchItem, searchValue])

  const commitHotelDateSelection = useCallback((
    hotelDatesOverride?: ListingDrawerHotelDateSelection,
  ) => {
    const resolvedOverride = isListingDrawerHotelDateSelection(hotelDatesOverride)
      ? hotelDatesOverride
      : undefined
    const nextSelection = resolvedOverride ?? getHotelDateSelectionFromSearchState(searchState, searchVertical)

    if (searchVertical === 'experience' && onDateChange) {
      onDateChange(nextSelection.type === 'specific' ? { checkIn: nextSelection.checkIn, checkOut: nextSelection.checkOut } : {})
      return
    }

    if (onHotelDateSelectionChange) {
      onHotelDateSelectionChange(nextSelection, pendingDateTargetRef.current)
      pendingDateTargetRef.current = undefined
      return
    }

    if (onDateChange) {
      if (nextSelection.type === 'specific') {
        onDateChange({
          checkIn: nextSelection.checkIn,
          checkOut: undefined,
        })
        return
      }

      onDateChange({
        checkIn: undefined,
        checkOut: undefined,
      })
    }
  }, [onDateChange, onHotelDateSelectionChange, searchState, searchVertical])

  const commitRoomsToParent = useCallback(() => {
    if (rooms.length > 0) {
      onRoomsChange?.(rooms)
    }

    const selectedGuests = sumUpOccupancies(rooms)
    if (!onRoomsChange && selectedGuests > 0) {
      onGuestsChange?.(Math.min(selectedGuests, maxGuests))
    }

    clearRoomErrors()
  }, [clearRoomErrors, maxGuests, onGuestsChange, onRoomsChange, rooms])

  const {
    isHotelDatePanelOpen,
    isHotelGuestsPanelOpen,
    isExperienceDatePanelOpen,
    restoreCommittedDates,
    handleRoomChange,
    openHotelDatePanel,
    closeHotelDatePanel,
    confirmHotelDatePanel,
    openHotelGuestsPanel,
    closeHotelGuestsPanel,
    confirmHotelGuestsPanel,
    openExperienceDatePanel,
    closeExperienceDatePanel,
    confirmExperienceDatePanel,
  } = useListingDrawerHotelSearchDraft({
    searchDispatch,
    committedHotelDateSelection,
    defaultRooms,
    isCompactDatePicker,
    commitHotelDateSelection,
    commitRoomsToParent,
    areRoomsValid,
    draftRooms: rooms,
    initiallyOpenGuestsPanel: initiallyIncompleteOccupancy && isCompactDatePicker,
  })

  // Clear Guests error once occupancy is valid again. Do not auto-show on mid-edit
  // invalidation — only initial seed, Apply, and Search set areRoomErrorsShown.
  useEffect(() => {
    if (!showGuests || !areRoomsValid || !searchState.areRoomErrorsShown) return
    clearRoomErrors()
  }, [areRoomsValid, clearRoomErrors, searchState.areRoomErrorsShown, showGuests])

  const searchButtonLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })

  const anytimeLabel = intl.formatMessage({
    id: 'RigImioe5lWt2RsFEZL9',
    defaultMessage: 'Anytime',
    description: 'Placeholder for date field indicating any date is acceptable',
  })

  const searchTypes = searchVertical === 'experience' ? TRIP_EXPERIENCE_TYPEAHEAD_SEARCH_TYPES : undefined
  const placeTypes = searchVertical === 'experience' ? TRIP_PLACE_TYPEAHEAD_TYPES : HOTEL_SEARCH_TYPEAHEAD_TYPES
  const recentSearchItems = useMemo(
    () => searchVertical === 'hotel' ? getRecentSearches() : undefined,
    [searchVertical],
  )
  const experienceDateFieldValue = useExperienceDateDisplayValue(
    searchState.checkinDate,
    searchState.checkoutDate,
  )
  const experienceTripDateRangeMoments = useMemo(() => {
    if (!experienceTripDateRange) return undefined

    return {
      start: moment(experienceTripDateRange.start),
      end: moment(experienceTripDateRange.end),
    }
  }, [experienceTripDateRange])
  const useMobileHotelSearchLayout = isCompactDatePicker && showCheckout && showGuests

  const closeMenu = useCallback(() => {
    discardPendingDateTarget()
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [discardPendingDateTarget])

  const closeDateMenu = useCallback(() => {
    restoreCommittedDates()
    closeMenu()
  }, [closeMenu, restoreCommittedDates])

  const openMenu = useCallback((menu: SearchMenuStates, event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation()
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [])

  const handleSearchItemChange = useCallback((nextSearchItem?: App.SearchItem) => {
    onSearchItemChange?.(nextSearchItem)
    setDraftSearchItem(nextSearchItem)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: nextSearchItem ?? buildDisplayOnlySearchItem(draftSearch),
    })
    if (nextSearchItem) {
      setDraftSearch(nextSearchItem.format.mainText)
    }
  }, [draftSearch, onSearchItemChange])

  const openHotelDatePicker = useCallback((propertySearchItem?: App.SearchItem) => {
    pendingDateTargetRef.current = propertySearchItem
    if (propertySearchItem) {
      handleSearchItemChange(propertySearchItem)
    }

    controlsContainerRef.current?.scrollIntoView({ block: 'nearest' })

    if (isCompactDatePicker) {
      openHotelDatePanel()
      return
    }

    openMenu(SearchMenuStates.Dates)
  }, [handleSearchItemChange, isCompactDatePicker, openHotelDatePanel, openMenu])

  useImperativeHandle(ref, () => ({ openHotelDatePicker }), [openHotelDatePicker])

  const applySearch = useCallback((hotelDatesOverride?: ListingDrawerHotelDateSelection) => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    const trimmedSearch = draftSearch.trim()
    const submitSearchItem = getSubmitSearchItem(trimmedSearch, draftSearchItem)
    const nextSelection = isListingDrawerHotelDateSelection(hotelDatesOverride)
      ? hotelDatesOverride
      : getHotelDateSelectionFromSearchState(searchState, searchVertical)

    if (!onSearchSubmit) {
      onSearch(trimmedSearch, submitSearchItem)
      commitHotelDateSelection(hotelDatesOverride)
      commitRoomsToParent()
      return
    }
    onSearchSubmit({
      searchValue: trimmedSearch,
      searchItem: submitSearchItem,
      hotelDateSelection: nextSelection,
      occupancies: rooms,
    })
  }, [
    areRoomsValid,
    commitHotelDateSelection,
    commitRoomsToParent,
    draftSearch,
    draftSearchItem,
    onSearch,
    onSearchSubmit,
    rooms,
    searchState,
    searchVertical,
  ])

  const unsetDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [])

  const unsetFlexibleDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [])

  const selectSpecificDates = useCallback(() => {
    unsetFlexibleDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
  }, [unsetFlexibleDates])

  const selectFlexibleDates = useCallback(() => {
    unsetDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
    if (searchState.flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY) {
      searchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
      })
    }
  }, [searchState.flexibleNights, unsetDates])

  const selectAnytimeDate = useCallback((shouldApply: boolean) => {
    unsetDates()
    unsetFlexibleDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    closeMenu()

    if (shouldApply) {
      applySearch({ type: 'anytime' })
    }
  }, [applySearch, closeMenu, unsetDates, unsetFlexibleDates])

  const handlePhraseChange = useCallback((value: string) => {
    setDraftSearch(value)
    onSearchItemChange?.(undefined)
    setDraftSearchItem((searchItem) => {
      if (!searchItem || searchItem.format.mainText.trim() === value.trim()) return searchItem
      return undefined
    })
  }, [onSearchItemChange])

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applySearch()
  }, [applySearch])

  const handleDatesApply = useCallback(() => {
    commitHotelDateSelection()
    closeMenu()
  }, [closeMenu, commitHotelDateSelection])

  const handleRoomsApply = useCallback(() => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    commitRoomsToParent()
    closeMenu()
  }, [commitRoomsToParent, areRoomsValid, closeMenu, searchDispatch])

  const handleDatesDecline = useCallback(() => {
    discardPendingDateTarget()
    restoreCommittedDates()
    closeMenu()
  }, [closeMenu, discardPendingDateTarget, restoreCommittedDates])

  const cancelHotelDatePanel = useCallback(() => {
    discardPendingDateTarget()
    closeHotelDatePanel()
  }, [closeHotelDatePanel, discardPendingDateTarget])

  const openGuestsPanel = useCallback(() => {
    discardPendingDateTarget()
    openHotelGuestsPanel()
  }, [discardPendingDateTarget, openHotelGuestsPanel])

  const openDateMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    openMenu(SearchMenuStates.Dates, event)
  }, [openMenu])

  /**
   * Shared by both desktop date inputs, which differ only in whether the
   * calendar inside the dropdown can draw the rest of the trip.
   */
  const desktopDateInputProps = useMemo(() => ({
    dropdownAnchorRef: controlsContainerRef,
    dropdownPortaled: searchVertical === 'hotel',
    onDateToggle: openDateMenu,
    isToggled: searchState.activeMenu === SearchMenuStates.Dates,
    onDatesApply: handleDatesApply,
    onDatesDecline: handleDatesDecline,
    anytimeDateSelected: searchState.isAnytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId: searchState.dateSearchOptionId,
    closeMenu: closeDateMenu,
    focusMonth: searchCalendarFocusDate,
  }), [
    closeDateMenu,
    handleDatesApply,
    handleDatesDecline,
    openDateMenu,
    searchCalendarFocusDate,
    searchState.activeMenu,
    searchState.dateSearchOptionId,
    searchState.isAnytimeDateSelected,
    searchVertical,
    selectAnytimeDate,
    selectFlexibleDates,
    selectSpecificDates,
  ])

  const hasTripContext = !!tripStops?.length

  const experienceWhenLabel = whenLabel ?? anytimeLabel

  const renderExperienceDateInput = useCallback(() => (
    <ExperienceDateInputContainer>
      {isCompactDatePicker ? (
        <ListingDrawerMobileExperienceDateFields
          isOpen={isExperienceDatePanelOpen}
          onOpen={openExperienceDatePanel}
          onClose={closeExperienceDatePanel}
          onConfirm={confirmExperienceDatePanel}
          whenLabel={experienceWhenLabel}
          anytimePlaceholder={anytimeLabel}
          tripDateRange={experienceTripDateRangeMoments}
          focusMonth={searchCalendarFocusDate}
        />
      ) : (
        <>
          <StyledSearchFormField
            ref={experienceDateTriggerRef}
            label={whenLabel}
            placeholder={anytimeLabel}
            value={experienceDateFieldValue}
            onClick={(event) => openMenu(SearchMenuStates.Dates, event)}
          />
          <ExperiencesSearchDateSelectDropdown
            anchorRef={controlsContainerRef}
            triggerRef={experienceDateTriggerRef}
            open={searchState.activeMenu === SearchMenuStates.Dates}
            onApply={handleDatesApply}
            onDecline={handleDatesDecline}
            tripDateRange={experienceTripDateRangeMoments}
            showNextBooking={false}
            focusMonth={searchCalendarFocusDate}
          />
        </>
      )}
    </ExperienceDateInputContainer>
  ), [
    anytimeLabel,
    closeExperienceDatePanel,
    confirmExperienceDatePanel,
    experienceDateFieldValue,
    experienceTripDateRangeMoments,
    experienceWhenLabel,
    handleDatesApply,
    handleDatesDecline,
    isCompactDatePicker,
    isExperienceDatePanelOpen,
    openExperienceDatePanel,
    openMenu,
    searchCalendarFocusDate,
    searchState.activeMenu,
    whenLabel,
  ])

  return (
    <ControlsContainer ref={controlsContainerRef}>
      <GlobalSearchContextProvider state={searchState} dispatch={searchDispatch}>
        <Form onSubmit={handleSubmit}>
          {showDestinationField && (
            <SearchFieldContainer>
              <SearchField
                label={searchLabel}
                placeholder={searchPlaceholder}
                onChange={handleSearchItemChange}
                onPhraseChange={handlePhraseChange}
                variant="dropdown"
                dropdownSize="fill-anchor"
                dropdownPortaled={searchVertical === 'hotel'}
                inputRef={searchInputRef}
                controlsContainerRef={controlsContainerRef}
                searchVertical={searchVertical}
                searchTypes={searchTypes}
                placeTypes={placeTypes}
                recentSearchItems={recentSearchItems}
                showNaturalLanguageSearchSuggestions={searchVertical === 'hotel'}
              />
            </SearchFieldContainer>
          )}

          {onHotelDateSelectionChange && showCheckout && (
            <DateInputContainer $compact={isCompactDatePicker}>
              {isCompactDatePicker ? (
                <ListingDrawerMobileHotelDateFields
                  isOpen={isHotelDatePanelOpen}
                  onOpen={openHotelDatePanel}
                  onClose={cancelHotelDatePanel}
                  onConfirm={confirmHotelDatePanel}
                  anytimePlaceholder={anytimeLabel}
                  focusMonth={searchCalendarFocusDate}
                />
              ) : hasTripContext ? (
                <TripContextSearchDateInput
                  {...desktopDateInputProps}
                  tripStops={tripStops}
                  contextStopId={contextStopId}
                />
              ) : (
                <SearchDateInputDesktop {...desktopDateInputProps} />
              )}
            </DateInputContainer>
          )}

          {onDateChange && !showCheckout && renderExperienceDateInput()}

          {showGuests && (
            <TravellersInputContainer $compact={isCompactDatePicker}>
              {isCompactDatePicker ? (
                <ListingDrawerMobileHotelGuestsFields
                  isOpen={isHotelGuestsPanelOpen}
                  onOpen={openGuestsPanel}
                  onClose={closeHotelGuestsPanel}
                  onConfirm={confirmHotelGuestsPanel}
                  hasError={searchState.areRoomErrorsShown && !areRoomsValid}
                  maxGuests={maxGuests}
                />
              ) : (
                <SearchTravellersInputDesktop
                  dropdownAnchorRef={controlsContainerRef}
                  dropdownPortaled={searchVertical === 'hotel'}
                  rooms={rooms}
                  onTravellersToggle={(event) => openMenu(SearchMenuStates.Rooms, event)}
                  isToggled={searchState.activeMenu === SearchMenuStates.Rooms}
                  onRoomChange={handleRoomChange}
                  onRoomsApply={handleRoomsApply}
                  closeMenu={closeMenu}
                  error={searchState.areRoomErrorsShown && !areRoomsValid}
                />
              )}
            </TravellersInputContainer>
          )}

          <SearchButton
            $compact={isCompactDatePicker}
            kind="primary"
            size="large"
            type="submit"
            fit={useMobileHotelSearchLayout ? 'full-width' : 'auto'}
          >
            {searchButtonLabel}
          </SearchButton>
        </Form>
      </GlobalSearchContextProvider>

    </ControlsContainer>
  )
})

export default ListingDrawerSearchControls
